/**
 * FikoTermalEleman.ts
 *
 * Thermal control element for SPH fluid simulation.
 * Provides heating, cooling, or constant temperature control within a spherical region.
 *
 * GPU Memory Layout: 64 bytes
 * ================================
 * Offset | Size | Field
 * -------|------|------------------
 * 0      | 4    | pozX (f32)
 * 4      | 4    | pozY (f32)
 * 8      | 4    | pozZ (f32)
 * 12     | 4    | etkilanicap (f32)
 * 16     | 4    | hedefSicaklik (f32)
 * 20     | 4    | transferKatsayisi (f32)
 * 24     | 4    | tipi (u32)
 * 28     | 4    | aktif (u32)
 * 32     | 32   | padding
 * ================================
 */

import { ISPHEleman, ElemanTipi, olusturId } from './ISPHEleman';

/**
 * Thermal element types
 */
export enum TermalTipi {
  SOGUTUCU = 0,        // Cooling element (removes heat)
  ISITICI = 1,         // Heating element (adds heat)
  SABIT_SICAKLIK = 2   // Constant temperature (thermostat)
}

/**
 * Configuration interface for thermal element
 */
export interface TermalElemanConfig {
  pozisyon?: { x: number; y: number; z: number };
  etkilanicap?: number;
  hedefSicaklik?: number;
  transferKatsayisi?: number;
  tipi?: TermalTipi;
  aktif?: boolean;
  ad?: string;
  renk?: string;
  gorunur?: boolean;
}

/**
 * Thermal control element for temperature manipulation
 *
 * This element applies thermal effects to particles within its influence radius:
 * - ISITICI: Increases particle temperature towards target
 * - SOGUTUCU: Decreases particle temperature towards target
 * - SABIT_SICAKLIK: Forces particles to target temperature (thermostat behavior)
 *
 * Transfer coefficient controls the rate of heat transfer (W/m²·K equivalent).
 * Higher values result in faster temperature changes.
 */
export class FikoTermalEleman implements ISPHEleman {
  readonly tip = ElemanTipi.TERMAL_ELEMAN;
  readonly id: string;

  // Spatial properties
  pozisyon: { x: number; y: number; z: number };
  etkilanicap: number;  // Influence radius in simulation units

  // Thermal properties
  hedefSicaklik: number;      // Target temperature (Celsius or Kelvin based on simulation)
  transferKatsayisi: number;   // Heat transfer coefficient (higher = faster)
  tipi: TermalTipi;

  // Control properties
  aktif: boolean;

  // Display properties
  ad: string;
  renk: string;         // Color for visualization (hex string)
  gorunur: boolean;     // Visibility in 3D scene

  // Statistics (not sent to GPU)
  private toplamEnerjiTransferi: number = 0;  // Total energy transferred
  private etkilenenParcacikSayisi: number = 0;

  constructor(config: Partial<TermalElemanConfig> = {}) {
    this.id = olusturId();
    this.pozisyon = config.pozisyon || { x: 0, y: 0, z: 0 };
    this.etkilanicap = config.etkilanicap ?? 0.1;
    this.hedefSicaklik = config.hedefSicaklik ?? 100;
    this.transferKatsayisi = config.transferKatsayisi ?? 100;
    this.tipi = config.tipi ?? TermalTipi.ISITICI;
    this.aktif = config.aktif ?? true;
    this.ad = config.ad || this.varsayilanAd();
    this.renk = config.renk || this.varsayilanRenk();
    this.gorunur = config.gorunur ?? true;
  }

  /**
   * Generate default name based on thermal type
   */
  private varsayilanAd(): string {
    const tip = TermalTipi[this.tipi];
    const kisaId = this.id.substring(0, 6);
    return `${tip}_${kisaId}`;
  }

  /**
   * Generate default color based on thermal type
   */
  private varsayilanRenk(): string {
    switch (this.tipi) {
      case TermalTipi.ISITICI:
        return '#FF4444';  // Red for heater
      case TermalTipi.SOGUTUCU:
        return '#4444FF';  // Blue for cooler
      case TermalTipi.SABIT_SICAKLIK:
        return '#44FF44';  // Green for thermostat
      default:
        return '#FFFFFF';
    }
  }

  /**
   * Convert thermal element data to GPU-compatible byte array (64 bytes)
   */
  toGPUBytes(): ArrayBuffer {
    const buffer = new ArrayBuffer(64);
    const view = new DataView(buffer);

    // Position (12 bytes)
    view.setFloat32(0, this.pozisyon.x, true);
    view.setFloat32(4, this.pozisyon.y, true);
    view.setFloat32(8, this.pozisyon.z, true);

    // Influence radius (4 bytes)
    view.setFloat32(12, this.etkilanicap, true);

    // Target temperature (4 bytes)
    view.setFloat32(16, this.hedefSicaklik, true);

    // Transfer coefficient (4 bytes)
    view.setFloat32(20, this.transferKatsayisi, true);

    // Thermal type (4 bytes)
    view.setUint32(24, this.tipi, true);

    // Active state (4 bytes)
    view.setUint32(28, this.aktif ? 1 : 0, true);

    // Padding (32 bytes) - leave as zeros

    return buffer;
  }

  /**
   * GPU struct byte size for this element
   */
  gpuStructBoyut(): number {
    return 64;
  }

  /**
   * Update energy transfer statistics
   * @param energyDelta - Energy transferred in this step (Joules)
   * @param particleCount - Number of particles affected
   */
  istatistikGuncelle(energyDelta: number, particleCount: number): void {
    this.toplamEnerjiTransferi += energyDelta;
    this.etkilenenParcacikSayisi = particleCount;
  }

  /**
   * Get current statistics
   */
  istatistikGetir(): { toplamEnerji: number; parcacikSayisi: number } {
    return {
      toplamEnerji: this.toplamEnerjiTransferi,
      parcacikSayisi: this.etkilenenParcacikSayisi
    };
  }

  /**
   * Reset statistics
   */
  istatistikSifirla(): void {
    this.toplamEnerjiTransferi = 0;
    this.etkilenenParcacikSayisi = 0;
  }

  /**
   * Calculate thermal power (W) based on temperature difference
   * @param ortamSicaklik - Ambient temperature
   */
  gucHesapla(ortamSicaklik: number): number {
    const deltaT = Math.abs(this.hedefSicaklik - ortamSicaklik);
    const alan = 4 * Math.PI * this.etkilanicap * this.etkilanicap;
    return this.transferKatsayisi * alan * deltaT;
  }

  /**
   * Validate thermal element configuration
   */
  dogrula(): { gecerli: boolean; hatalar: string[] } {
    const hatalar: string[] = [];

    if (this.etkilanicap <= 0) {
      hatalar.push('Etki yarıçapı pozitif olmalıdır');
    }

    if (this.etkilanicap > 2.0) {
      hatalar.push('Etki yarıçapı çok büyük (>2.0)');
    }

    if (this.hedefSicaklik < -273.15) {
      hatalar.push('Hedef sıcaklık mutlak sıfırın altında olamaz');
    }

    if (this.hedefSicaklik > 10000) {
      hatalar.push('Hedef sıcaklık çok yüksek (>10000)');
    }

    if (this.transferKatsayisi < 0) {
      hatalar.push('Transfer katsayısı negatif olamaz');
    }

    if (this.transferKatsayisi > 10000) {
      hatalar.push('Transfer katsayısı çok yüksek (>10000) - simülasyon kararsız olabilir');
    }

    if (!Object.values(TermalTipi).includes(this.tipi)) {
      hatalar.push('Geçersiz termal eleman tipi');
    }

    // Validate hex color format
    if (!/^#[0-9A-F]{6}$/i.test(this.renk)) {
      hatalar.push('Geçersiz renk formatı (hex bekleniyor: #RRGGBB)');
    }

    return {
      gecerli: hatalar.length === 0,
      hatalar
    };
  }

  /**
   * Create a deep clone of this thermal element
   */
  klonla(): FikoTermalEleman {
    const yeni = new FikoTermalEleman({
      pozisyon: { ...this.pozisyon },
      etkilanicap: this.etkilanicap,
      hedefSicaklik: this.hedefSicaklik,
      transferKatsayisi: this.transferKatsayisi,
      tipi: this.tipi,
      aktif: this.aktif,
      ad: `${this.ad}_kopya`,
      renk: this.renk,
      gorunur: this.gorunur
    });

    // Copy statistics
    yeni.toplamEnerjiTransferi = this.toplamEnerjiTransferi;
    yeni.etkilenenParcacikSayisi = this.etkilenenParcacikSayisi;

    return yeni;
  }

  /**
   * Serialize to JSON
   */
  toJSON(): any {
    return {
      tip: 'TERMAL_ELEMAN',
      id: this.id,
      pozisyon: this.pozisyon,
      etkilanicap: this.etkilanicap,
      hedefSicaklik: this.hedefSicaklik,
      transferKatsayisi: this.transferKatsayisi,
      tipi: TermalTipi[this.tipi],
      aktif: this.aktif,
      ad: this.ad,
      renk: this.renk,
      gorunur: this.gorunur,
      istatistik: {
        toplamEnerjiTransferi: this.toplamEnerjiTransferi,
        etkilenenParcacikSayisi: this.etkilenenParcacikSayisi
      }
    };
  }

  /**
   * Deserialize from JSON
   */
  static fromJSON(json: any): FikoTermalEleman {
    const tipi = typeof json.tipi === 'string'
      ? TermalTipi[json.tipi as keyof typeof TermalTipi]
      : json.tipi;

    const eleman = new FikoTermalEleman({
      pozisyon: json.pozisyon,
      etkilanicap: json.etkilanicap,
      hedefSicaklik: json.hedefSicaklik,
      transferKatsayisi: json.transferKatsayisi,
      tipi: tipi,
      aktif: json.aktif,
      ad: json.ad,
      renk: json.renk,
      gorunur: json.gorunur
    });

    // Restore statistics if available
    if (json.istatistik) {
      eleman.toplamEnerjiTransferi = json.istatistik.toplamEnerjiTransferi || 0;
      eleman.etkilenenParcacikSayisi = json.istatistik.etkilenenParcacikSayisi || 0;
    }

    return eleman;
  }

  /**
   * Get list of adjustable parameters
   */
  parametreListesi(): Array<{ ad: string; tip: string; deger: any }> {
    return [
      { ad: 'pozisyon.x', tip: 'number', deger: this.pozisyon.x },
      { ad: 'pozisyon.y', tip: 'number', deger: this.pozisyon.y },
      { ad: 'pozisyon.z', tip: 'number', deger: this.pozisyon.z },
      { ad: 'etkilanicap', tip: 'number', deger: this.etkilanicap },
      { ad: 'hedefSicaklik', tip: 'number', deger: this.hedefSicaklik },
      { ad: 'transferKatsayisi', tip: 'number', deger: this.transferKatsayisi },
      { ad: 'tipi', tip: 'enum', deger: TermalTipi[this.tipi] },
      { ad: 'aktif', tip: 'boolean', deger: this.aktif },
      { ad: 'ad', tip: 'string', deger: this.ad },
      { ad: 'renk', tip: 'color', deger: this.renk },
      { ad: 'gorunur', tip: 'boolean', deger: this.gorunur }
    ];
  }

  /**
   * Set a parameter value
   */
  parametreAyarla(parametreAdi: string, deger: any): boolean {
    try {
      switch (parametreAdi) {
        case 'pozisyon.x':
          this.pozisyon.x = Number(deger);
          return true;
        case 'pozisyon.y':
          this.pozisyon.y = Number(deger);
          return true;
        case 'pozisyon.z':
          this.pozisyon.z = Number(deger);
          return true;
        case 'etkilanicap':
          this.etkilanicap = Math.max(0.001, Number(deger));
          return true;
        case 'hedefSicaklik':
          this.hedefSicaklik = Number(deger);
          return true;
        case 'transferKatsayisi':
          this.transferKatsayisi = Math.max(0, Number(deger));
          return true;
        case 'tipi':
          if (typeof deger === 'string') {
            this.tipi = TermalTipi[deger as keyof typeof TermalTipi];
          } else {
            this.tipi = Number(deger);
          }
          // Update color based on new type
          if (!this.renk || this.renk === this.varsayilanRenk()) {
            this.renk = this.varsayilanRenk();
          }
          return true;
        case 'aktif':
          this.aktif = Boolean(deger);
          return true;
        case 'ad':
          this.ad = String(deger);
          return true;
        case 'renk':
          // Validate hex color
          if (typeof deger === 'string' && /^#[0-9A-F]{6}$/i.test(deger)) {
            this.renk = deger.toUpperCase();
            return true;
          }
          return false;
        case 'gorunur':
          this.gorunur = Boolean(deger);
          return true;
        default:
          return false;
      }
    } catch (e) {
      console.error(`Parametre ayarlama hatası: ${parametreAdi}`, e);
      return false;
    }
  }

  /**
   * Get a parameter value by name
   */
  parametreAl(ad: string): any {
    return (this as any)[ad];
  }
}

/**
 * Factory function to create a thermal element with default values
 */
export function varsayilanTermalEleman(config: Partial<TermalElemanConfig> = {}): FikoTermalEleman {
  return new FikoTermalEleman(config);
}

/**
 * Factory: Create heater element
 */
export function isiticiOlustur(
  pozisyon: { x: number; y: number; z: number },
  sicaklik: number = 200,
  yaricap: number = 0.1
): FikoTermalEleman {
  return new FikoTermalEleman({
    pozisyon,
    hedefSicaklik: sicaklik,
    etkilanicap: yaricap,
    tipi: TermalTipi.ISITICI,
    transferKatsayisi: 100
  });
}

/**
 * Factory: Create cooler element
 */
export function sogutucuOlustur(
  pozisyon: { x: number; y: number; z: number },
  sicaklik: number = 5,
  yaricap: number = 0.1
): FikoTermalEleman {
  return new FikoTermalEleman({
    pozisyon,
    hedefSicaklik: sicaklik,
    etkilanicap: yaricap,
    tipi: TermalTipi.SOGUTUCU,
    transferKatsayisi: 100
  });
}

/**
 * Factory: Create thermostat element
 */
export function termostatOlustur(
  pozisyon: { x: number; y: number; z: number },
  sicaklik: number = 20,
  yaricap: number = 0.1
): FikoTermalEleman {
  return new FikoTermalEleman({
    pozisyon,
    hedefSicaklik: sicaklik,
    etkilanicap: yaricap,
    tipi: TermalTipi.SABIT_SICAKLIK,
    transferKatsayisi: 500  // Higher for tighter control
  });
}

/**
 * Helper function to get thermal type name in Turkish
 */
export function termalTipiAdi(tip: TermalTipi): string {
  const adlar: Record<TermalTipi, string> = {
    [TermalTipi.ISITICI]: 'Isıtıcı',
    [TermalTipi.SOGUTUCU]: 'Soğutucu',
    [TermalTipi.SABIT_SICAKLIK]: 'Termostat'
  };
  return adlar[tip] || 'Bilinmeyen';
}

/**
 * Helper function to get thermal type description
 */
export function termalTipiAciklama(tip: TermalTipi): string {
  const aciklamalar: Record<TermalTipi, string> = {
    [TermalTipi.ISITICI]: 'Parçacıkları hedef sıcaklığa kadar ısıtır',
    [TermalTipi.SOGUTUCU]: 'Parçacıkları hedef sıcaklığa kadar soğutur',
    [TermalTipi.SABIT_SICAKLIK]: 'Parçacıkları hedef sıcaklıkta sabit tutar (termostat)'
  };
  return aciklamalar[tip] || '';
}
